import { Button, DynamicContainer, Form, FormContext } from '@hai/ui-react';
import { ButtonStateType } from '@hai/ui-react/dist/types';
import { FormikProps } from 'formik';
import { isNil } from 'ramda';
import React, { Ref, forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { SelectOptions } from 'src/js/component/base/form/form-select';
import { FormTextfield } from 'src/js/component/base/form/form-textfield';
import { FormBuilder, FormFieldType, FormLayout } from 'src/js/component/form-builder';
import { triggerEvent, watchEvent } from 'src/js/events';
import { useStores } from 'src/js/hook/use-stores';
import videoInputs from 'src/js/pages/streaming/video-input/video-input-store';
import { FormChangeEvent } from 'src/js/util/global-type';
import { isNilOrEmpty } from 'src/js/util/global-util';
import { submitAndValidate, validateFormContext } from 'src/js/validator/validator';
import { EssenceType, EssenceViewModel, essenceValidationSchema } from './essence-models';
import essenceModels, { createEssence, resolutions, updateEssence } from './essence-store';
import { useWatchObject } from 'src/js/store/use-watch';
import { useMutable } from 'src/js/hook/use-mutable';

interface Props {
  model: EssenceViewModel;
  onCancel?: VoidFunction;
  nmosServiceActive: boolean;
}

export interface EssenceAddFormRef {
  submitAddEssence: () => Promise<void>;
}

export const EssenceSettings = forwardRef((props: Props, ref: Ref<EssenceAddFormRef>) => {
  const { model, onCancel } = props;
  const { t } = useTranslation();
  const { sessionStore } = useStores();
  const nmosServiceActive = props.nmosServiceActive;
  const formRef = useRef<FormikProps<EssenceViewModel>>();
  const [settings, setSettings] = useState<EssenceViewModel>(model);
  const [buttonState, setButtonState] = useState<ButtonStateType>(undefined);
  const [packetTimeWasTweaked, setPacketTimeWasTweaked] = useMutable(false);

  if (settings.packetTimeActual !== model.packetTimeActual) {
    // allow live update for the actual value only
    if (formRef.current && formRef.current) {
      formRef.current.initialValues.packetTimeActual = model.packetTimeActual;
    }
  }

  const mediaTypeOptions = [
    { value: 'video', label: t('essence.mediaTypes.video') },
    { value: 'audio', label: t('essence.mediaTypes.audio') },
    { value: 'data', label: t('essence.mediaTypes.data') },
  ];

  const packetTimeOptions = [
    { value: 1, label: '1 ms', disabled:false },
    { value: 2, label: '125 µs' },
  ];

  const [resetOnNextReset, setResetOnNextReset] = useMutable(false);
  watchEvent('preset-load', () => {
    setResetOnNextReset(true);
  });
  
  useWatchObject(essenceModels, (_updates) => {
    // compare if any changes from upstream since form was opened (settings vs _updates) and notify
    if (resetOnNextReset()) {
      const essense = essenceModels.find((e) => e.id === model.id);
      if (essense) {
        setSettings(essense); // update edit view with newest model
        formRef.current.resetForm({ values: essense });
        setResetOnNextReset(false);
      }
    }
  });
  
  const [inputChannelOptions, setInputChannelOptions] = useState<SelectOptions>();
  const getInterfaceOptions = () => {
    setInputChannelOptions(
      videoInputs.map((input) => ({
        value: input.id,
        label: input.name,
      })),
    );
  };

  const getRTPPayloadDefault = (type: string) => {
    switch (type) {
      case EssenceType.video:
        return 96;
      case EssenceType.audio:
        return 98;
      case EssenceType.data:
        return 100;
      default:
        return 0;
    }
  };

  useEffect(() => {
    getInterfaceOptions();
  }, []);

  const newEssence = settings.id === -1;

  const handleSubmit = (values: EssenceViewModel) => {
    setButtonState('pending');

    const data = {
      ...values,
      rtpPayload: isNilOrEmpty(values.rtpPayload.toString()) ? getRTPPayloadDefault(values.type) : values.rtpPayload,
    };

    if (newEssence) {
      return createEssence(data, t).then((res) => {
        setButtonState(undefined);
        if (res.ok) {
          triggerEvent('preset-update');
          setPacketTimeWasTweaked(false);
        }
        return res;
      });
    } else {
      return updateEssence(data, t).then((res) => {
        if (res.ok) {
          const essense = essenceModels.find((e) => e.id === model.id);
          setSettings(essense); // update edit view with newest model
          formRef.current.resetForm({ values: essense });
          triggerEvent('preset-update');
          setPacketTimeWasTweaked(false);
        }
        setButtonState(undefined);
        return res;
      });
    }
  };

  const submitAddEssence = () => {
    return submitAndValidate(formRef.current, t);
  };

  useImperativeHandle(ref, () => ({
    submitAddEssence,
  }));

  const handleChangeType = (formEvent: FormChangeEvent) => {
    const rtpPayload = getRTPPayloadDefault(formEvent.target.value);
    formRef.current.setFieldValue('rtpPayload', rtpPayload);
    formRef.current.setFieldTouched('rtpPayload');
  };

  if (isNil(inputChannelOptions)) {
    return null;
  }

  return (
    <Form
      initialValues={settings}
      defaultValidation={true}
      handleSubmit={handleSubmit}
      className="hai-mt-n8"
      restValidationProps={{
        enableReinitialize: false, // "false" requires manual resetForm() above. We can then do partial update of the form while it's opened.
        validationSchema: essenceValidationSchema(t),
        innerRef: formRef,
      }}
    >
      <FormContext.Consumer>
        {(formContext: FormikProps<EssenceViewModel>) => {
          const values = formContext.values;
          let packetTimeActual = undefined;
          packetTimeOptions[0].disabled = false;
          if (formContext.initialValues['packetTime'] !== formContext.initialValues['packetTimeActual'] && formContext.initialValues['packetTimeActual'] != null
            && values.numOfChannels > 10 && values.packetTime === 1
          ) {
            packetTimeActual = values.packetTimeActual === 2 ? packetTimeOptions[1].label : undefined;
            if (values.packetTimeActual === 2 && values.numOfChannels > 10) {
              packetTimeOptions[0].disabled = true;
            }
          }
          if (values.numOfChannels > 10) {
            packetTimeOptions[0].disabled = true;
            if (newEssence && packetTimeWasTweaked() === false && values.packetTime !== 2) {
              formContext.setFieldValue('packetTime', 2);
              formContext.setFieldTouched('packetTime');
              setPacketTimeWasTweaked(true);
            }
          } else if (values.numOfChannels <= 10 && packetTimeWasTweaked() === true && values.packetTime === 2) {
            formContext.setFieldValue('packetTime', 1);
            formContext.setFieldTouched('packetTime');
            setPacketTimeWasTweaked(false); // put back to default behavior
          }          

          return (
            <>
              <DynamicContainer minColumns={2} maxColumns={2} className="mk-mt-panel-name-align">
                <FormTextfield
                  name="name"
                  mask
                  title={t('general.name')}
                  className="max-width"
                  autoFocus={newEssence}
                  disabled={nmosServiceActive}
                />
                {!newEssence && !sessionStore.isUser() && (
                  <div className="d-flex ml-auto hai-mt-panel-apply-sticky">
                    <Button className="hai-mr-6" onClick={onCancel}>
                      {t('general.cancel')}
                    </Button>
                    <Button
                      variant="primary"
                      onClick={() => validateFormContext(formContext, t)}
                      disabled={!formContext.dirty || nmosServiceActive}
                      state={buttonState}
                    >
                      {t('general.apply')}
                    </Button>
                  </div>
                )}
              </DynamicContainer>
              <FormBuilder
                translationPrefixLabels="essence.settings"
                translationPrefixEnums="essence.enums"
                layout={FormLayout.THREE_COLUMNS_GRID}
                fields={[
                  {
                    type: FormFieldType.TITLE,
                    label: t('essence.sections.essence'),
                    className: 'hai-mb-0',
                    disabled: nmosServiceActive,
                  },
                  {
                    type: FormFieldType.DROPDOWN,
                    name: 'type',
                    options: mediaTypeOptions,
                    onChange: handleChangeType,
                    disabled: nmosServiceActive,
                  },
                  {
                    type: FormFieldType.CHECKBOX,
                    name: 'autoStart',
                    when: newEssence,
                    disabled: nmosServiceActive,
                  },
                  {
                    type: FormFieldType.INPUT_TEXT,
                    name: 'numOfChannels',
                    hint: t('essence.settings.numOfChannelsExplain'),
                    when: values.type === EssenceType.audio,
                    required: true,
                    disabled: nmosServiceActive,
                    onChange: (e:any) => {
                      // console.log('e', e.target.value);
                      if (e.target.value > 10 && values.packetTime === 1) {
                        formContext.setFieldValue('packetTime', 2);
                        formContext.setFieldTouched('packetTime');
                      setPacketTimeWasTweaked(true);
                      }
                    }
                  },
                  {
                    type: FormFieldType.DROPDOWN,
                    name: 'packetTime',
                    options: packetTimeOptions,
                    when: values.type === EssenceType.audio,
                    disabled: nmosServiceActive,
                    hint: packetTimeActual != null ? t('essence.settings.packetTimeMismatch', {actual: packetTimeActual}) : undefined,
                    onChange: (_e:any) => {
                      setPacketTimeWasTweaked(false);
                    }
                  },
                  {
                    type: FormFieldType.TITLE,
                    label: t('essence.sections.settings'),
                    disabled: nmosServiceActive,
                  },
                  {
                    type: FormFieldType.INPUT_TEXT,
                    name: 'address',
                    required: true,
                    disabled: nmosServiceActive,
                  },
                  {
                    type: FormFieldType.INPUT_TEXT,
                    name: 'port',
                    required: true,
                    hint: t('essence.settings.portExplain'),
                    disabled: nmosServiceActive,
                  },
                  {
                    type: FormFieldType.INPUT_TEXT,
                    name: 'rtpPayload',
                    placeholder: getRTPPayloadDefault(values.type).toString(),
                    hint: t('essence.settings.rtpPayloadExplain'),
                    disabled: nmosServiceActive,
                  },
                  {
                    type: FormFieldType.INPUT_TEXT,
                    placeholder: t('general.any'),
                    name: 'sourceAddress',
                    disabled: nmosServiceActive,
                  },
                  {
                    type: FormFieldType.DROPDOWN,
                    name: 'inputChannel',
                    options: inputChannelOptions,
                    disabled: nmosServiceActive,
                  },
                  {
                    type: FormFieldType.DROPDOWN,
                    name: 'inputFormat',
                    options: resolutions,
                    direction: 'up',
                    when: values.type === EssenceType.video,
                    disabled: nmosServiceActive,
                  },
                ]}
              />
            </>
          );
        }}
      </FormContext.Consumer>
    </Form>
  );
});
EssenceSettings.displayName = 'EssenceSettings';
